import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import './Header.css';
import logoImage from '../assets/SOPHIEwhite.png';

const Header = () => {
    const headerRef = useRef(null);
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginForm, setLoginForm] = useState({
        email: '',
        password: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginError, setLoginError] = useState('');

    useEffect(() => {
        const handleScroll = () => {
            if (headerRef.current) {
                if (window.scrollY > 10) {
                    headerRef.current.classList.add('scrolled');
                } else {
                    headerRef.current.classList.remove('scrolled');
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Function to toggle the mobile menu
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Function to close the mobile menu (useful for link clicks)
    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    // Function to toggle login modal
    const toggleLoginModal = () => {
        setShowLoginModal(!showLoginModal);
        setLoginError(''); // Clear any previous errors
    };

    const handleLoginInputChange = (e) => {
        const { name, value } = e.target;
        setLoginForm({
            ...loginForm,
            [name]: value
        });
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setLoginError('');

        try {
            const auth = getAuth();
            const userCredential = await signInWithEmailAndPassword(
                auth,
                loginForm.email,
                loginForm.password
            );
            
            // Login successful
            console.log('[SOPHIE-CLIENT] User logged in successfully:', userCredential.user.uid);
            
            // Close the modal
            setShowLoginModal(false);
            
            // Navigate to DemoCRM page
            navigate('/demo-crm');
        } catch (error) {
            console.error('[SOPHIE-CLIENT] Login error:', error);
            
            // Display user-friendly error message
            switch(error.code) {
                case 'auth/invalid-email':
                    setLoginError('Please enter a valid email address.');
                    break;
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                    setLoginError('Invalid email or password. Please try again.');
                    break;
                default:
                    setLoginError('An error occurred during login. Please try again later.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const scrollToSection = (e, sectionId) => {
        e.preventDefault();
        closeMobileMenu(); // Close menu when a link is clicked
        // Check if on the home page before scrolling
        if (window.location.pathname === '/') {
            const section = document.getElementById(sectionId);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            // If not on home page, navigate first, then scroll (might need more complex logic)
            window.location.href = `/#${sectionId}`;
        }
    };

    const scrollToTop = (e) => {
        e.preventDefault();
        closeMobileMenu(); // Close menu when logo is clicked
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <header className="app-header" ref={headerRef}>
                <div className="nav-container">
                    {/* Logo link */}
                    <a href="/" onClick={scrollToTop} className="nav-logo-link">
                        <img src={logoImage} alt="SOPHIE Logo" className="nav-logo" />
                    </a>

                    {/* Desktop Navigation */}
                    <nav>
                      <div className="nav-dropdown">
                        <button className="nav-link nav-dropdown-toggle">Products</button>
                        <div className="nav-dropdown-menu nav-dropdown-menu-wide">
                          <a
                            href="#crm-automation"
                            onClick={(e) => scrollToSection(e, 'crm-automation')}
                            className="nav-dropdown-item nav-dropdown-item-rich"
                          >
                            <span className="nav-dropdown-icon">
                              {/* CRM Automation Icon (example: file-text) */}
                              <svg width="28" height="28" fill="none" stroke="#ff6699" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            </span>
                            <span>
                              <span className="nav-dropdown-title">CRM Automation</span>
                              <span className="nav-dropdown-desc">AI-powered document collection, naming, and filing for Salesforce & HubSpot.</span>
                            </span>
                          </a>
                          <a
                            href="#invoice-management"
                            onClick={(e) => scrollToSection(e, 'invoice-management')}
                            className="nav-dropdown-item nav-dropdown-item-rich"
                          >
                            <span className="nav-dropdown-icon">
                              {/* Invoice Management Icon (example: receipt) */}
                              <svg width="28" height="28" fill="none" stroke="#1a93cc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                            </span>
                            <span>
                              <span className="nav-dropdown-title">Invoice Management</span>
                              <span className="nav-dropdown-desc">Automatically match, review, and validate invoices with ERP & CRM integration.</span>
                            </span>
                          </a>
                        </div>
                      </div>
                      <a href="#about-us" onClick={(e) => scrollToSection(e, 'about-us')} className="nav-link">About us</a>
                      <a href="#our-values" onClick={(e) => scrollToSection(e, 'our-values')} className="nav-link">Our Values</a>
                      <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="nav-link">Contact</a>
                      <button onClick={toggleLoginModal} className="login-button">Login</button>
                    </nav>

                    {/* Burger Menu Button */}
                    <button className="burger-menu" onClick={toggleMobileMenu} aria-label="Toggle menu">
                        {/* You can use an icon here, e.g., three lines */}
                        &#9776; {/* Simple burger icon */}
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                <div className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
                    <a href="#crm-automation" onClick={(e) => scrollToSection(e, 'crm-automation')} className="nav-link">CRM Automation</a>
                    <a href="#invoice-management" onClick={(e) => scrollToSection(e, 'invoice-management')} className="nav-link">Invoice Management</a>
                    <a href="#about-us" onClick={(e) => scrollToSection(e, 'about-us')} className="nav-link">About us</a>
                    <a href="#our-values" onClick={(e) => scrollToSection(e, 'our-values')} className="nav-link">Our Values</a>
                    <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="nav-link">Contact</a>
                    {/* Add login button to mobile menu */}
                    <button onClick={toggleLoginModal} className="mobile-login-button">Login</button>
                </div>
            </header>

            {/* Login Modal */}
            {showLoginModal && (
                <div className="login-modal-overlay">
                    <div className="login-modal">
                        <button className="close-modal" onClick={toggleLoginModal}>&times;</button>
                        <h2>Login to Sophie AI</h2>
                        <form onSubmit={handleLoginSubmit} className="login-form">
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={loginForm.email}
                                    onChange={handleLoginInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={loginForm.password}
                                    onChange={handleLoginInputChange}
                                    required
                                />
                            </div>
                            {loginError && <div className="login-error">{loginError}</div>}
                            <button 
                                type="submit" 
                                className="login-submit-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Logging in...' : 'Login'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;